import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft, Loader2, Info, Activity, TrendingUp, Ruler, Scale, User, Calendar, Download,
} from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import GrowthCurveChart from "@/components/GrowthCurveChart";
import PaywallModal from "@/components/PaywallModal";
import DOBPicker from "@/components/DOBPicker";
import { ageFromDOB, toISODate, parseISODate, formatAgeDisplay } from "@/utils/age";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GrowthAssessmentPage = () => {
  useTheme();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [heightResult, setHeightResult] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  const [dob, setDob] = useState(parseISODate(searchParams.get("dob")));
  const [heightForm, setHeightForm] = useState({
    name: searchParams.get("name") || "",
    current_height: "",
    present_weight: "",
    bone_age: searchParams.get("bone_age") || "",
    gender: searchParams.get("gender") || "",
    ethnicity: "ind",
    father_height: "",
    mother_height: "",
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await api.post(`${API}/boneah/generate-report`, {
        bone_data: null,
        height_data: heightResult || null,
      }, { responseType: "blob", timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Growth_Report_${heightResult?.name || "Kid"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        try {
          const me = await api.get(`${API}/payments/me`);
          setCreditBalance(me.data.balance || 0);
        } catch (_err) { /* ignore */ }
        setPaywallOpen(true);
        return;
      }
      let msg = "Failed to generate report";
      if (error.code === "ERR_NETWORK") msg = "Network error — please check your internet connection.";
      else if (error.response?.data) {
        try { const txt = await error.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch (_err) { /* keep default msg */ }
      }
      toast.error(msg);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleHeightSubmit = async (e) => {
    e.preventDefault();
    if (!dob) { toast.error("Please pick the child's date of birth"); return; }
    const ageInfo = ageFromDOB(dob);
    if (!ageInfo || ageInfo.decimalYears <= 0 || ageInfo.decimalYears > 18) {
      toast.error("Date of birth must give an age between 0 and 18 years"); return;
    }
    const required = ["name", "current_height", "present_weight", "bone_age", "gender"];
    for (const f of required) {
      if (!heightForm[f]) { toast.error(`Please fill in ${f.replace(/_/g, " ")}`); return; }
    }
    setLoading(true);
    setHeightResult(null);
    try {
      const payload = {
        ...heightForm,
        chronological_age: ageInfo.decimalYears,
        date_of_birth: toISODate(dob),
        current_height: parseFloat(heightForm.current_height),
        present_weight: parseFloat(heightForm.present_weight),
        bone_age: parseFloat(heightForm.bone_age),
        father_height: heightForm.father_height ? parseFloat(heightForm.father_height) : null,
        mother_height: heightForm.mother_height ? parseFloat(heightForm.mother_height) : null,
        // True only when the user came here from the Bone Age X-ray flow
        // (the BoneAgePage redirect sets ?xray=1 in the URL).
        xray_provided: searchParams.get("xray") === "1",
      };
      const response = await api.post(`${API}/boneah/predict-height-weight`, payload, { timeout: 30000 });
      setHeightResult(response.data);
      toast.success("Growth prediction complete!");
    } catch (error) {
      let msg = "Prediction failed. Please try again.";
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network")) {
        msg = "Network error — please check your internet connection.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getGHIColor = (ghi) => {
    if (ghi >= 80) return "#4ECDC4";
    if (ghi >= 60) return "#FFE66D";
    return "#FF6B6B";
  };

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#4ECDC4] transition-colors mb-6 text-sm" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Back to tots360
            </Link>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3" data-testid="growth-page-title">
              Growth <span className="text-[#4ECDC4]">Assessment</span>
            </h1>
            <p className="text-white/50 text-lg">Predict adult height & assess growth using Bayley-Pinneau and parental midpoint</p>
          </div>

          <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl">
            <CardHeader>
              <CardTitle className="text-xl text-white font-display">Growth Inputs</CardTitle>
              <CardDescription className="text-white/40">Enter the child&apos;s measurements and bone-age value (from a clinical X-ray or our Bone Age Prediction tool)</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleHeightSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-white/70">
                      <Calendar className="w-4 h-4 text-[#4ECDC4]" /> Date of Birth
                    </Label>
                    <DOBPicker value={dob} onChange={setDob} testId="growth-dob" />
                  </div>
                  {[
                    { key: "name", label: "Kid Name", icon: User, placeholder: "Enter name", type: "text" },
                    { key: "gender", label: "Gender", icon: User, type: "select", options: [{ v: "male", l: "Male" }, { v: "female", l: "Female" }] },
                    { key: "bone_age", label: "Bone Age (years)", icon: Calendar, placeholder: "e.g., 7.5", type: "number", step: "0.5" },
                    { key: "current_height", label: "Current Height (cm)", icon: Ruler, placeholder: "e.g., 106", type: "number" },
                    { key: "present_weight", label: "Present Weight (kg)", icon: Scale, placeholder: "e.g., 19", type: "number" },
                    { key: "father_height", label: "Father's Height (cm, optional)", icon: User, placeholder: "e.g., 176", type: "number" },
                    { key: "mother_height", label: "Mother's Height (cm, optional)", icon: User, placeholder: "e.g., 150", type: "number" },
                  ].map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70">
                        <field.icon className="w-4 h-4 text-[#4ECDC4]" /> {field.label}
                      </Label>
                      {field.type === "select" ? (
                        <Select value={heightForm[field.key]} onValueChange={(v) => setHeightForm(p => ({ ...p, [field.key]: v }))}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`height-select-${field.key}`}>
                            <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a2e] border-white/10">
                            {field.options.map(o => (<SelectItem key={o.v} value={o.v} className="text-white">{o.l}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input type={field.type} step={field.step} placeholder={field.placeholder}
                          value={heightForm[field.key]} onChange={(e) => setHeightForm(p => ({ ...p, [field.key]: e.target.value }))}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          data-testid={`height-input-${field.key}`} />
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-white/70"><Info className="w-4 h-4 text-[#4ECDC4]" /> Ethnicity</Label>
                  <Select value={heightForm.ethnicity} onValueChange={(v) => setHeightForm(p => ({ ...p, ethnicity: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="height-select-ethnicity">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      {[
                        { v: "ind", l: "Indian" }, { v: "caueurn", l: "Caucasian (N. European)" },
                        { v: "caueurc", l: "Caucasian (C. European)" }, { v: "asijap", l: "Asian (Japanese)" },
                        { v: "asichi", l: "Asian (Chinese)" }, { v: "afram", l: "African American" },
                      ].map(o => (<SelectItem key={o.v} value={o.v} className="text-white">{o.l}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] text-lg py-6 rounded-xl font-semibold"
                  disabled={loading} data-testid="height-submit-btn">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>) : "Run Growth Assessment"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <AnimatePresence>
            {heightResult && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
                <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="height-result-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-white font-display">
                      <TrendingUp className="w-5 h-5 text-[#4ECDC4]" /> Growth Report for {heightResult.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: "Predicted Final Height", value: `${heightResult.ahp_xplusp} cm`, color: "#4ECDC4" },
                        { label: "Growth Remaining", value: `${heightResult.growth_remaining_years} yrs`, color: "#FFE66D" },
                        { label: "APHV", value: `${heightResult.aphv} yrs`, color: "#A855F7" },
                        { label: "GHI Score", value: `${heightResult.growth_health_index}/100`, color: getGHIColor(heightResult.growth_health_index) },
                      ].map((m, i) => (
                        <div key={i} className="p-4 rounded-xl border text-center" style={{ background: `${m.color}10`, borderColor: `${m.color}30` }}>
                          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: m.color }}>{m.label}</p>
                          <p className="text-2xl font-bold text-white">{m.value}</p>
                        </div>
                      ))}
                    </div>

                    <GrowthCurveChart heightResult={heightResult} boneResult={null} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <h3 className="text-white font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-[#4ECDC4]" /> Bone Age Assessment</h3>
                        {[
                          { label: "Chronological Age", value: dob ? formatAgeDisplay(dob) : `${heightResult.chronological_age} years` },
                          { label: "Bone Age", value: `${heightResult.bone_age} years` },
                          { label: "Difference", value: `${heightResult.bone_age_difference > 0 ? "+" : ""}${heightResult.bone_age_difference} years` },
                          { label: "Maturity Status", value: heightResult.maturity_status },
                          { label: "Carpal Bone Maturity", value: heightResult.carpal_bone_maturity },
                        ].map((item, i) => (
                          <div key={i} className="flex justify-between items-center p-2 bg-white/[0.02] rounded-lg">
                            <span className="text-sm text-white/40">{item.label}</span>
                            <span className="text-sm font-medium text-white">{item.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-white font-semibold flex items-center gap-2"><Ruler className="w-4 h-4 text-[#4ECDC4]" /> Height Predictions</h3>
                        {[
                          { label: "AHP (X-ray)", value: `${heightResult.ahp_xray} cm` },
                          ...(heightResult.ahp_parental ? [{ label: "AHP (Parental)", value: `${heightResult.ahp_parental} cm` }] : []),
                          { label: "Present Height Given", value: `${heightResult.present_height} cm` },
                          { label: "Predicted Final Height (Combined)", value: `${heightResult.ahp_xplusp} cm` },
                          { label: "Present Weight", value: `${heightResult.present_weight} kg` },
                          { label: "Predicted Weight", value: `${heightResult.predicted_weight} kg` },
                          { label: "Weight Status", value: heightResult.weight_status },
                        ].map((item, i) => (
                          <div key={i} className="flex justify-between items-center p-2 bg-white/[0.02] rounded-lg">
                            <span className="text-sm text-white/40">{item.label}</span>
                            <span className="text-sm font-medium text-white">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-white">Growth Health Index (GHI)</span>
                        <span className="text-sm font-bold" style={{ color: getGHIColor(heightResult.growth_health_index) }}>
                          {heightResult.growth_health_index >= 80 ? "Excellent" : heightResult.growth_health_index >= 60 ? "Good" : heightResult.growth_health_index >= 40 ? "Fair" : "Poor"}
                        </span>
                      </div>
                      <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${heightResult.growth_health_index}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className="h-full rounded-full" style={{ backgroundColor: getGHIColor(heightResult.growth_health_index) }} />
                      </div>
                      <p className="text-xs text-white/30 mt-1">{heightResult.growth_health_index}/100</p>
                    </div>

                    <div className="pt-4">
                      <Button onClick={downloadPDF} disabled={pdfLoading}
                        className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold py-5 text-base" data-testid="height-download-pdf-btn">
                        {pdfLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
                        Download Growth Report (PDF)
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
      <Footer />
      <Toaster position="top-right" />
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} balance={creditBalance} />
    </div>
  );
};

export default GrowthAssessmentPage;
